import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportMerchandisingCsv,
  fetchMerchandisingQueryGroup,
  importMerchandisingCsv,
  previewMerchandisingCsv,
  saveMerchandisingQueryGroup,
} from '../state/merchandisingSlice';
import {
  queryGroupToWorkbench,
  queryWorkbenchSharedRule,
} from '../model/queryRuleWorkbench';
import { runWithConcurrency } from '../model/rulePortability';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import { normalizeLanguageScope } from '../model/languageScope';

const FORMAT = 'kea.merchandising.query-groups';

const uuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
  });
};

const download = (value) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], {
    type: 'application/json',
  }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'kea-merchandising-search-rules.json';
  anchor.click();
  URL.revokeObjectURL(url);
};

const downloadCsv = (name, value) => {
  const url = URL.createObjectURL(new Blob([value], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const portableQueryGroup = (response) => {
  const model = queryGroupToWorkbench(response);
  return {
    name: model.name || null,
    note: model.note || null,
    queries: model.queries,
    rule: queryWorkbenchSharedRule(model),
  };
};

export const parseQueryGroupManifest = (text) => {
  const payload = JSON.parse(text);
  if (!payload || payload.format !== FORMAT || payload.version !== 1
    || !Array.isArray(payload.groups) || payload.groups.length < 1 || payload.groups.length > 100) {
    throw new Error('This file is not a supported search-rules export.');
  }
  payload.groups.forEach((group) => {
    if (!group || !Array.isArray(group.queries) || group.queries.length < 1
      || group.queries.length > 50 || group.queries.some((query) => typeof query !== 'string' || !query.trim())
      || !group.rule || typeof group.rule !== 'object' || Array.isArray(group.rule)) {
      throw new Error('The search-rules export contains an invalid group.');
    }
  });
  return payload.groups;
};

const QueryGroupTransferControls = ({
  groups, languageId, onComplete, actions = ['duplicate', 'export', 'import'],
}) => {
  const dispatch = useDispatch();
  const csvEnabled = useSelector((state) => Boolean(state.merchandising?.capability?.csv));
  const t = useLocalizedMessage();
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const scopeLanguageId = normalizeLanguageScope(languageId);

  const exportGroups = async () => {
    setBusy(true);
    const loaded = await runWithConcurrency(groups, async (group) => {
      const action = await dispatch(fetchMerchandisingQueryGroup({
        id: group.id,
        languageId: normalizeLanguageScope(group.language_id, scopeLanguageId),
      }));
      if (!fetchMerchandisingQueryGroup.fulfilled.match(action)) {
        throw new Error(action.payload?.message || 'Search rule could not be loaded.');
      }
      return portableQueryGroup(action.payload);
    }, { concurrency: 3 });
    const successful = loaded.filter((row) => row.status === 'succeeded').map((row) => row.value);
    const failed = loaded.length - successful.length;
    if (successful.length) download({ format: FORMAT, version: 1, groups: successful });
    setBusy(false);
    if (failed) setResult({ kind: 'export', successful: successful.length, failed });
  };

  const exportCsv = async () => {
    setBusy(true);
    const action = await dispatch(exportMerchandisingCsv({
      type: 'query_rules',
      ids: groups.map((group) => String(group.id)),
      languageId: scopeLanguageId,
    }));
    setBusy(false);
    if (!exportMerchandisingCsv.fulfilled.match(action)) {
      setResult({ kind: 'error', message: action.payload?.message || 'CSV export failed.' });
      return;
    }
    downloadCsv(action.payload.file_name, action.payload.content);
    if (action.payload.skipped) {
      setResult({
        kind: 'csv-export',
        successful: action.payload.exported,
        failed: action.payload.skipped,
        errors: action.payload.errors || [],
      });
    }
  };

  const createGroups = async (portableGroups, duplicate = false) => {
    setBusy(true);
    const created = await runWithConcurrency(portableGroups, async (group) => {
      const action = await dispatch(saveMerchandisingQueryGroup({
        clientMutationId: uuid(),
        languageId: scopeLanguageId,
        name: duplicate && group.name ? `${group.name.slice(0, 151)} (copy)` : group.name,
        note: group.note,
        queries: group.queries,
        rule: group.rule,
      }));
      if (!saveMerchandisingQueryGroup.fulfilled.match(action)) {
        throw new Error(action.payload?.message || 'Search-rule draft could not be created.');
      }
      return action.payload;
    }, { concurrency: 3 });
    const successful = created.filter((row) => row.status === 'succeeded').length;
    setBusy(false);
    setResult({ kind: duplicate ? 'duplicate' : 'import', successful, failed: created.length - successful });
    if (successful && onComplete) onComplete();
  };

  const duplicateGroups = async () => {
    setBusy(true);
    const loaded = await runWithConcurrency(groups, async (group) => {
      const action = await dispatch(fetchMerchandisingQueryGroup({
        id: group.id,
        languageId: normalizeLanguageScope(group.language_id, scopeLanguageId),
      }));
      if (!fetchMerchandisingQueryGroup.fulfilled.match(action)) {
        throw new Error(action.payload?.message || 'Search rule could not be loaded.');
      }
      return portableQueryGroup(action.payload);
    }, { concurrency: 3 });
    setBusy(false);
    const portable = loaded.filter((row) => row.status === 'succeeded').map((row) => row.value);
    if (portable.length) await createGroups(portable, true);
    else setResult({ kind: 'duplicate', successful: 0, failed: loaded.length });
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      if (/\.csv$/i.test(file.name) || file.type.includes('csv')) {
        if (!csvEnabled) throw new Error('CSV import requires a newer plugin version.');
        setBusy(true);
        const action = await dispatch(previewMerchandisingCsv({
          type: 'query_rules', csv: text, languageId: scopeLanguageId,
        }));
        setBusy(false);
        if (!previewMerchandisingCsv.fulfilled.match(action)) {
          throw new Error(action.payload?.message || 'CSV preview failed.');
        }
        setResult({ kind: 'csv-preview', preview: action.payload, csv: text, mutationId: uuid() });
        return;
      }
      await createGroups(parseQueryGroupManifest(text));
    } catch (error) {
      setBusy(false);
      setResult({ kind: 'error', message: error.message });
    }
  };

  const applyCsv = async () => {
    setBusy(true);
    const action = await dispatch(importMerchandisingCsv({
      type: 'query_rules',
      csv: result.csv,
      clientMutationId: result.mutationId,
      languageId: scopeLanguageId,
    }));
    setBusy(false);
    if (!importMerchandisingCsv.fulfilled.match(action)) {
      setResult({ kind: 'error', message: action.payload?.message || 'CSV import failed.' });
      return;
    }
    setResult({ kind: 'import', successful: action.payload.created, failed: 0 });
    if (onComplete) onComplete();
  };

  return (
    <>
      <div className="mp-transfer-actions">
        {actions.includes('duplicate') && <Button disabled={busy || !groups.length} onClick={duplicateGroups}>{t('merchandising.transfer.duplicate')}</Button>}
        {actions.includes('export') && <Button disabled={busy || !groups.length} onClick={exportGroups}>{t('merchandising.transfer.exportJson')}</Button>}
        {actions.includes('export') && csvEnabled && <Button disabled={busy || !groups.length} onClick={exportCsv}>{t('merchandising.transfer.exportCsv')}</Button>}
        {actions.includes('import') && <Button disabled={busy} onClick={() => fileInput.current.click()}>{t('merchandising.transfer.import')}</Button>}
        {actions.includes('import') && <input ref={fileInput} hidden type="file" accept={csvEnabled ? 'application/json,text/csv,.json,.csv' : 'application/json,.json'} onChange={importFile} aria-label={t('merchandising.workbench.importSearchRules')} />}
      </div>
      <Dialog className="mp-dialog" open={Boolean(result)} onClose={() => !busy && setResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('merchandising.workbench.queryTransferTitle')}</DialogTitle>
        <DialogContent>
          {result?.kind === 'error' && <div className="mp-inline-error" role="alert">{result.message}</div>}
          {result?.kind === 'csv-preview' && (
            <>
              <p>{t('merchandising.transfer.csvPreview', { rules: result.preview.item_count, rows: result.preview.row_count })}</p>
              {!result.preview.valid && <div className="mp-inline-error" role="alert">{t('merchandising.transfer.csvInvalid')}</div>}
              {(result.preview.errors || []).map((error, index) => (
                <div className="mp-transfer-csv-error" key={`${error.code}-${error.row}-${index}`}>
                  <strong>
                    {error.row
                      ? `${t('merchandising.transfer.csvRow', { row: error.row })}${error.column ? ` · ${error.column}` : ''}`
                      : t('merchandising.transfer.csvFile')}
                  </strong>
                  <span>{error.message}</span>
                  <code>{error.code}</code>
                </div>
              ))}
              <p>{t('merchandising.transfer.draftOnly')}</p>
            </>
          )}
          {result && !['error', 'csv-preview'].includes(result.kind) && (
            <p>{t('merchandising.workbench.queryTransferResult', { successful: result.successful, failed: result.failed })}</p>
          )}
          {result?.kind === 'csv-export' && (result.errors || []).map((error, index) => (
            <div className="mp-transfer-csv-error" key={`${error.code}-${error.id}-${index}`}>
              <strong>{error.code}</strong>
              <span>{error.message}</span>
            </div>
          ))}
        </DialogContent>
        <DialogActions className="mp-dialog-actions">
          <Button variant="outlined" disabled={busy} onClick={() => setResult(null)}>{t('merchandising.transfer.close')}</Button>
          {result?.kind === 'csv-preview' && result.preview.valid && (
            <Button color="primary" variant="contained" disabled={busy} onClick={applyCsv}>
              {t('merchandising.transfer.createDrafts', { count: result.preview.item_count })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

QueryGroupTransferControls.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  languageId: PropTypes.string,
  onComplete: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.oneOf(['duplicate', 'export', 'import'])),
};
QueryGroupTransferControls.defaultProps = {
  languageId: 'default',
  onComplete: null,
  actions: undefined,
};

export default QueryGroupTransferControls;
